import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Checkout } from '@/@types/Checkouts/list-checkouts';
import { Coupon, CouponPayload } from '@/@types/Coupons';
import { AxiosErrorResponse } from '@/@types/Errors';
import { OfferData } from '@/@types/Products/Offers';
import { toast } from '@/hooks/use-toast';
import { CheckoutsService } from '@/services/checkouts.service';
import { CouponsService } from '@/services/coupons.service';
import { OffersService } from '@/services/offers.service';
import useModalStore from '@/store/useModalStore';

export const useFetchCoupons = (page: number = 1) => {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['coupons', id, page],
    queryFn: async () => {
      return await CouponsService.getCoupons({ productId: id, page });
    },
    placeholderData: (previous) => previous,
  });

  const totalPages = data?.meta?.last_page ?? 1;

  return {
    coupons: data?.data ?? [],
    currentPage: data?.meta?.current_page ?? page,
    totalPages,
    total: data?.meta?.total ?? 0,
    /** Quem manda no tamanho da página é a API — o `paginate()` do controller. */
    hasPagination: totalPages > 1,
    isLoading,
    isFetching,
    error: error?.message,
  };
};

const NO_OFFERS: OfferData[] = [];

/** Todas as ofertas do produto: a API pagina sem aceitar `per_page`. */
export const useCouponOffers = () => {
  const { id } = useParams<{ id: string }>();

  const { data } = useQuery({
    queryKey: ['coupon-offers', id],
    queryFn: async () => {
      const productId = id as string;
      const firstPage = await OffersService.getOffers({ productId, page: 1 });
      const lastPage = firstPage.meta?.last_page ?? 1;

      const nextPages = await Promise.all(
        Array.from({ length: Math.max(0, lastPage - 1) }, (_, index) =>
          OffersService.getOffers({ productId, page: index + 2 })
        )
      );

      return [firstPage, ...nextPages].flatMap((response) => response.data);
    },
    enabled: Boolean(id),
  });

  return data ?? NO_OFFERS;
};

const NO_CHECKOUTS: Checkout[] = [];

/** Todos os checkouts do produto, para os links do cupom: a API pagina sem aceitar `per_page`. */
export const useCouponCheckouts = () => {
  const { id } = useParams<{ id: string }>();

  const { data } = useQuery({
    queryKey: ['coupon-checkouts', id],
    queryFn: async () => {
      const productId = id as string;
      const firstPage = await CheckoutsService.getCheckouts({ productId, page: 1 });
      const lastPage = firstPage.meta?.last_page ?? 1;

      const nextPages = await Promise.all(
        Array.from({ length: Math.max(0, lastPage - 1) }, (_, index) =>
          CheckoutsService.getCheckouts({ productId, page: index + 2 })
        )
      );

      return [firstPage, ...nextPages].flatMap((response) => response.data);
    },
    enabled: Boolean(id),
  });

  return data ?? NO_CHECKOUTS;
};

interface UseCouponMutationsProps {
  couponToEdit?: Coupon | null;
  /** Recebe os erros de validação da API (422) para marcar os campos do formulário. */
  onValidationError?: (errors: Record<string, string[]>) => void;
}

export const useCouponMutations = ({
  couponToEdit = null,
  onValidationError,
}: UseCouponMutationsProps = {}) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { closeModal } = useModalStore();
  const { id } = useParams<{ id: string }>();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['coupons'] });
  };

  const handleError = (
    error: AxiosError<AxiosErrorResponse>,
    fallback: string
  ) => {
    const fieldErrors = error?.response?.data?.errors;

    if (fieldErrors && onValidationError) {
      onValidationError(fieldErrors);
    }

    toast({
      variant: 'error',
      description: error?.response?.data?.message || fallback,
    });
  };

  const createMutation = useMutation({
    mutationFn: async (data: CouponPayload) => {
      return await CouponsService.createCoupon({ productId: id, data });
    },
    onSuccess: (response) => {
      toast({
        variant: 'success',
        description: t('createCoupon.saveSuccess'),
      });

      if (response?.warning) {
        toast({ variant: 'error', description: response.warning });
      }

      invalidate();
      closeModal('createCoupon');
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      handleError(error, t('createCoupon.createError'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: CouponPayload) => {
      return await CouponsService.updateCoupon({
        productId: id,
        couponId: couponToEdit?.id,
        data,
      });
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        description: t('createCoupon.saveSuccess'),
      });
      invalidate();
      closeModal('editCoupon');
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      handleError(error, t('createCoupon.updateError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (couponId: string) => {
      return await CouponsService.deleteCoupon({ productId: id, couponId });
    },
    onSuccess: () => {
      invalidate();
      toast({
        variant: 'success',
        description: t('createCoupon.deleteSuccess'),
      });
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        variant: 'error',
        description:
          error?.response?.data?.message || t('createCoupon.deleteError'),
      });
    },
  });

  const submitCoupon = (payload: CouponPayload) => {
    if (couponToEdit) {
      updateMutation.mutate(payload);

      return;
    }

    createMutation.mutate(payload);
  };

  const handleDelete = (couponId: string) => {
    deleteMutation.mutate(couponId);
  };

  return {
    submitCoupon,
    handleDelete,
    isSaving: createMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
